import { toFiniteNumber } from "../lib/dexaCalculations.js";

function value(source, fallback = 0) {
  return toFiniteNumber(source) ?? fallback;
}

function numberLabel(number, digits = 1) {
  const numeric = toFiniteNumber(number);
  return numeric !== null
    ? numeric.toLocaleString("es-CO", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
    : "—";
}

export function CompositionDonut({ total }) {
  const fat = Math.max(0, value(total.fatPercent));
  const lean = Math.max(0, value(total.leanPercent));
  const bone = Math.max(0, value(total.bmcPercent));
  const sum = fat + lean + bone || 1;
  const fatArc = fat / sum * 100;
  const leanArc = lean / sum * 100;
  const gradient = `conic-gradient(
    #d8652f 0 ${fatArc}%,
    #3f7f72 ${fatArc}% ${fatArc + leanArc}%,
    #2f5f80 ${fatArc + leanArc}% 100%
  )`;
  return (
    <div className="composition-donut" role="img" aria-label="Distribución de grasa, masa magra y contenido mineral óseo">
      <div className="composition-donut__chart" style={{ background: gradient }}>
        <div><strong>{numberLabel(total.totalMassKg, 1)}</strong><span>kg DXA</span></div>
      </div>
      <dl>
        <div><dt><i className="swatch swatch--fat" />Grasa</dt><dd>{numberLabel(total.fatMassKg, 1)} kg</dd></div>
        <div><dt><i className="swatch swatch--lean" />Magra</dt><dd>{numberLabel(total.leanMassKg, 1)} kg</dd></div>
        <div><dt><i className="swatch swatch--bone" />CMO</dt><dd>{numberLabel(total.bmcKg, 2)} kg</dd></div>
      </dl>
    </div>
  );
}

function SymmetryRow({ label, left, right, unit = "kg" }) {
  const leftNumber = toFiniteNumber(left);
  const rightNumber = toFiniteNumber(right);
  const leftValue = leftNumber ?? 0;
  const rightValue = rightNumber ?? 0;
  const maximum = Math.max(leftValue, rightValue, 1);
  return (
    <div className="symmetry-row">
      <span>{label}</span>
      <div className="symmetry-row__axis">
        <div className="symmetry-row__left" style={{ width: `${leftValue / maximum * 50}%` }} />
        <i />
        <div className="symmetry-row__right" style={{ width: `${rightValue / maximum * 50}%` }} />
      </div>
      <small>{numberLabel(leftNumber, unit === "%" ? 1 : 2)} {unit} · {numberLabel(rightNumber, unit === "%" ? 1 : 2)} {unit}</small>
    </div>
  );
}

export function SymmetryChart({ regions }) {
  const gramsToKg = (grams) => {
    const numeric = toFiniteNumber(grams);
    return numeric === null ? null : numeric / 1000;
  };
  return (
    <div className="symmetry-chart">
      <div className="symmetry-chart__legend"><span>Izquierda</span><span>Derecha</span></div>
      <SymmetryRow label="Brazos · masa magra" left={gramsToKg(regions.leftArm?.leanMassG)} right={gramsToKg(regions.rightArm?.leanMassG)} />
      <SymmetryRow label="Piernas · masa magra" left={gramsToKg(regions.leftLeg?.leanMassG)} right={gramsToKg(regions.rightLeg?.leanMassG)} />
      <SymmetryRow label="Brazos · grasa" left={regions.leftArm?.fatPercent} right={regions.rightArm?.fatPercent} unit="%" />
      <SymmetryRow label="Piernas · grasa" left={regions.leftLeg?.fatPercent} right={regions.rightLeg?.fatPercent} unit="%" />
    </div>
  );
}

export function BoneGauge({ score, mode }) {
  const numeric = toFiniteNumber(score);
  const min = mode === "t" ? -4 : -4;
  const max = 3;
  const bounded = numeric !== null ? Math.min(max, Math.max(min, numeric)) : min;
  const position = (bounded - min) / (max - min) * 100;
  return (
    <div className={`bone-gauge bone-gauge--${mode}`}>
      <div className="bone-gauge__track">
        <span className="bone-gauge__review" />
        <span className="bone-gauge__attention" />
        <span className="bone-gauge__favorable" />
        {numeric !== null ? <i style={{ left: `${position}%` }} /> : null}
      </div>
      <div className="bone-gauge__labels">
        <span>-4</span>
        <strong>{mode === "t" ? "T-score" : "Z-score"}</strong>
        <span>+3</span>
      </div>
    </div>
  );
}

export function RegionalBmdChart({ regions }) {
  const average = (left, right) => {
    const leftValue = toFiniteNumber(left);
    const rightValue = toFiniteNumber(right);
    return leftValue === null || rightValue === null ? null : (leftValue + rightValue) / 2;
  };
  const entries = [
    ["Brazos", average(regions.leftArm?.bmd, regions.rightArm?.bmd)],
    ["Costillas", average(regions.leftRibs?.bmd, regions.rightRibs?.bmd)],
    ["Columna dorsal", toFiniteNumber(regions.thoracicSpine?.bmd)],
    ["Columna lumbar", toFiniteNumber(regions.lumbarSpine?.bmd)],
    ["Pelvis", toFiniteNumber(regions.pelvis?.bmd)],
    ["Piernas", average(regions.leftLeg?.bmd, regions.rightLeg?.bmd)],
  ];
  const maximum = Math.max(...entries.map(([, bmd]) => bmd ?? 0), 1.6);
  return (
    <div className="regional-bmd-chart" role="img" aria-label="Comparación de densidad mineral ósea por regiones">
      {entries.map(([label, bmd]) => (
        <div key={label}>
          <span>{label}</span>
          <i><b style={{ width: `${(bmd ?? 0) / maximum * 100}%` }} /></i>
          <strong>{numberLabel(bmd, 3)}</strong>
        </div>
      ))}
    </div>
  );
}
